interface MapProps {
  /**
   * Additional CSS classes to apply to the map container
   */
  className?: string;

  /**
   * Custom inline styles for the map container
   */
  style?: React.CSSProperties;

  /**
   * Unique identifier for the map component
   */
  id?: string;

  /**
   * Accessibility label for the map
   * @default "Map showing Tennis Gourmet location"
   */
  ariaLabel?: string;

  /**
   * Google Maps API key for enhanced functionality
   * If not provided, falls back to basic embed
   */
  apiKey?: string;

  /**
   * Map zoom level
   * @default 15
   */
  zoom?: number;

  /**
   * Map type (roadmap, satellite, hybrid, terrain)
   * @default "roadmap"
   */
  mapType?: "roadmap" | "satellite" | "hybrid" | "terrain";

  /**
   * Custom marker title for accessibility
   * @default "Tennis Gourmet - Via Gregorini 89, 23017 Morbegno (SO), Italy"
   */
  markerTitle?: string;

  /**
   * Whether to enable all map controls (zoom, street view, etc.)
   * @default true
   */
  enableControls?: boolean;
}

// Tennis Gourmet location coordinates
const TENNIS_GOURMET_LOCATION = {
  address: "Via Gregorini 89, 23017 Morbegno (SO), Italy",
  lat: 46.1364,
  lng: 9.5669,
};

/**
 * Map component - Interactive Google Maps component showing Tennis Gourmet location
 *
 * Features:
 * - Google Maps integration with Tennis Gourmet location marker
 * - Follows Tennis Gourmet atomic design patterns
 * - Uses Tailwind CSS with @apply directives and tg- prefixes
 * - TypeScript interface for comprehensive props validation
 * - Full accessibility support with proper ARIA labels
 * - Responsive design maintaining aspect ratios
 * - Uses secure Google Maps Embed (no API key required)
 * - Loading states and error handling
 * - Tennis Gourmet color scheme integration
 *
 * @example
 * ```tsx
 * // Basic usage (secure embed without API key)
 * <Map />
 *
 * // Footer usage with responsive design
 * <Map className="col-span-12 aspect-[16/9] xl:col-span-6" />
 *
 * // Contact page usage with custom styling
 * <Map className="order-last aspect-[16/9] rounded-2xl xl:aspect-square xl:rounded-4xl" />
 *
 * // With custom zoom and map type
 * <Map
 *   zoom={16}
 *   mapType="satellite"
 *   ariaLabel="Tennis Gourmet location map"
 * />
 * ```
 */
export function Map({
  className = "",
  style,
  id,
  ariaLabel = "Map showing Tennis Gourmet location",
  apiKey,
  zoom = 15,
  mapType = "roadmap",
  markerTitle = "Tennis Gourmet - Via Gregorini 89, 23017 Morbegno (SO), Italy",
  enableControls = true,
}: MapProps) {
  // Note: API key removed for security - using basic embed only
  // This provides the same functionality without exposing API credentials
  const effectiveApiKey = apiKey; // Only use explicitly provided API key, no env fallback
  // Generate Google Maps Embed URL
  const generateEmbedUrl = () => {
    // If API key is provided, use the more advanced embed API
    if (effectiveApiKey) {
      const advancedUrl = "https://www.google.com/maps/embed/v1/place";
      const advancedParams = new URLSearchParams({
        key: effectiveApiKey,
        q: TENNIS_GOURMET_LOCATION.address,
        zoom: zoom.toString(),
        maptype: mapType,
      });
      const finalUrl = `${advancedUrl}?${advancedParams.toString()}`;
      console.log("Using Google Maps Embed API with key:", finalUrl);
      return finalUrl;
    }

    // Fallback using Google Maps standard embed with proper interactive controls
    // This approach uses the Google Maps URL that gets automatically converted to an embed
    const params = new URLSearchParams({
      q: TENNIS_GOURMET_LOCATION.address,
      output: "embed",
      z: zoom.toString(),
      t: mapType === "satellite" ? "k" : "m", // k for satellite, m for map
    });

    // Add control parameters if controls are disabled
    if (!enableControls) {
      params.set("iwloc", "near"); // Disable info window controls
    }

    const finalUrl = `https://maps.google.com/maps?${params.toString()}`;
    console.log("Using Google Maps standard embed:", finalUrl);
    return finalUrl;
  };

  // Generate Google Maps link for fallback
  const generateMapsLink = () => {
    const params = new URLSearchParams({
      q: TENNIS_GOURMET_LOCATION.address,
    });
    return `https://www.google.com/maps?${params.toString()}`;
  };

  return (
    <div
      className={`tg-map ${className}`}
      style={style}
      id={id}
      role="application"
      aria-label={ariaLabel}
    >
      <div className="tg-map__container">
        <iframe
          src={generateEmbedUrl()}
          width="100%"
          height="100%"
          style={{
            border: 0,
            pointerEvents: "auto",
            touchAction: "auto",
          }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={markerTitle}
          aria-label={`Interactive map showing ${markerTitle}`}
          onError={() => {
            console.warn("Map failed to load, fallback link available");
          }}
        />

        {/* Fallback link for accessibility and when map fails to load */}
        <a
          href={generateMapsLink()}
          target="_blank"
          rel="noopener noreferrer"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-10 focus:rounded focus:bg-white focus:px-2 focus:py-1 focus:text-black focus:shadow-lg"
          aria-label={`Open ${markerTitle} in Google Maps (opens in new tab)`}
        >
          Open in Google Maps
        </a>
      </div>
    </div>
  );
}
